import React, { useState, useEffect } from "react";
import { NotificationManager } from "react-notifications";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import useApi from "../Api/Api";
import SideLabelInput from "../Components/SideLabelInput";
import Button from "../Components/Button";

const EditArtist = ({
  handleShowEditForm,
  getArtist,
  artist,
  setArtist,
  getArtistSplits,
  tempManagerPercentage,
  setTempManagerPercentage,
}) => {
  const [isFetching, setIsFetching] = useState(false);
  const [errorMessage, setErrorMessage] = useState(false);
  const api = useApi();
  const [artistSplit, setArtistSplit] = useState({});
  const [tempSplitPercentage, setTempSplitPercentage] = useState(0);
  const [splitChanged, setSplitChanged] = useState(false);

  useEffect(() => {
    api.get(`/artist_splits?artist_id=${artist.id}`).then((r) => {
      if (r.data.length > 0) {
        setTempSplitPercentage(parseFloat(r.data[0]?.percentage * 100));
        setArtistSplit(r.data[0]);
      } else {
        setTempSplitPercentage();
      }
    });
  }, [api, artist.id]);

  const handleChange = ({ target: { id, value } }) => {
    setArtist({
      ...artist,
      [id]: value,
    });
  };

  const handleSplitChange = ({ target: { id, value } }) => {
    setSplitChanged(true);
    setArtistSplit({
      ...artistSplit,
      [id]: value,
    });
  };

  const handleTempSplitPercentageChange = ({ target: { value } }) => {
    setSplitChanged(true);
    setTempSplitPercentage(value);
  };

  const handleTempManagerPercentageChange = ({ target: { value } }) => {
    setTempManagerPercentage(value);
  };

  const handleBooleanChange = ({ target: { name, value } }) => {
    setArtist({
      ...artist,
      [name]: !!value,
    });
  };

  const handleSubmit = (event) => {
    delete artistSplit.id;
    setIsFetching(true);
    splitChanged &&
      api
        .put(`/artists/${artist.id}`, {
          artist: {
            ...artist,
            manager_commission_percentage: tempManagerPercentage / 100,
            artist_splits_attributes: [
              {
                ...artistSplit,
                percentage: tempSplitPercentage / 100,
              },
            ],
          },
        })
        .then((response) => {
          NotificationManager.success(
            "Artist successfully updated",
            "Success",
            2000
          );
          handleShowEditForm();
          getArtist();
          getArtistSplits();
          return response.data;
        })
        .catch((error) => {
          setErrorMessage(error.response.data);
        })
        .finally(() => {});
    !splitChanged &&
      api
        .put(`/artists/${artist.id}`, {
          artist: {
            ...artist,
            manager_commission_percentage: tempManagerPercentage / 100,
          },
        })
        .then((response) => {
          NotificationManager.success(
            "Artist successfully updated",
            "Success",
            2000
          );
          handleShowEditForm();
          getArtist();
          getArtistSplits();
          return response.data;
        })
        .catch((error) => {
          setErrorMessage(error.response.data);
        })
        .finally(() => {});
    setIsFetching(false);
    event.preventDefault();
  };

  return (
    <div>
      <div className="-mt-20 side-modal">
        <div className="flex-col">
          <header className="py-6 px-4 text-lg mb-2 bg-blue-500 text-white">
            <button onClick={handleShowEditForm} className="float-right -mt-4">
              <FontAwesomeIcon icon={faTimes}></FontAwesomeIcon>
            </button>
            <div>Edit Artist</div>
            <div className="text-sm">
              You can modify this information later if needed
            </div>
          </header>
          <div className="mt-8 px-4 tracking-normal	font-medium">
            Artist Information
          </div>
          <hr className="my-3 mx-4"></hr>
          <form className="mx-4" id="submit" onSubmit={handleSubmit}>
            <div className="center">
              <div className="my-5">
                <SideLabelInput
                  id="name"
                  type="name"
                  label="Name"
                  value={artist.name ?? ""}
                  name="name"
                  onChange={handleChange}
                  disabled={isFetching}
                  required
                />
                {errorMessage?.errors?.name && (
                  <div className="text-red-500 pt-2">
                    Name {errorMessage.errors.name}
                  </div>
                )}
              </div>
              <div className="my-5">
                <SideLabelInput
                  id="tempSplitPercentage"
                  type="tempSplitPercentage"
                  label="Artist Split Percentage"
                  value={tempSplitPercentage ?? ""}
                  name="tempSplitPercentage"
                  onChange={handleTempSplitPercentageChange}
                  disabled={isFetching}
                  required
                />
              </div>
              <div className="flex items-center text-xs my-5">
                <label htmlFor="effective_date" className="w-1/3">
                  Effective Date
                </label>
                <input
                  value={artistSplit?.effective_date?.split("T")[0] ?? ""}
                  type="date"
                  id="effective_date"
                  name="effective_date"
                  className="h-9 pl-2 ml-5 font-light rounded-md border w-full justify-self-end"
                  onChange={handleSplitChange}
                  disabled={isFetching}
                  required
                ></input>
              </div>
              <div className="my-5">
                <SideLabelInput
                  id="manager_payment"
                  type="manager_payment"
                  label="Road manager daily rate when artist vends"
                  value={artist.manager_payment ?? ""}
                  name="manager_payment"
                  onChange={handleChange}
                  disabled={isFetching}
                  className="my-5"
                />
              </div>
              <div className="my-5">
                <SideLabelInput
                  id="venue_vended_manager_payment"
                  type="venue_vended_manager_payment"
                  label="Road manager daily rate when artist doesn't vend"
                  value={artist.venue_vended_manager_payment ?? ""}
                  name="venue_vended_manager_payment"
                  onChange={handleChange}
                  disabled={isFetching}
                  className="my-5"
                />
              </div>
              <div className="my-5">
                <SideLabelInput
                  id="manager_commission_percentage"
                  type="manager_commission_percentage"
                  label="Road manager commission percentage"
                  value={tempManagerPercentage ?? ""}
                  name="manager_commission_percentage"
                  onChange={handleTempManagerPercentageChange}
                  disabled={isFetching}
                  className="my-5"
                />
              </div>
              <div className="my-5">
                <SideLabelInput
                  id="notes"
                  type="notes"
                  label="Notes"
                  value={artist.notes ?? ""}
                  name="notes"
                  onChange={handleChange}
                  disabled={isFetching}
                  className="my-5"
                />
              </div>
              <div className="my-5">
                <SideLabelInput
                  id="business_name"
                  type="business_name"
                  label="Business Name"
                  value={artist.business_name ?? ""}
                  name="business_name"
                  onChange={handleChange}
                  disabled={isFetching}
                />
              </div>
              <div className="my-5">
                <SideLabelInput
                  id="tax_id"
                  type="tax_id"
                  label="Tax ID"
                  value={artist.tax_id ?? ""}
                  name="tax_id"
                  onChange={handleChange}
                  disabled={isFetching}
                />
              </div>
              <div className="my-5">
                <SideLabelInput
                  id="street_address"
                  type="street_address"
                  label="Street Address"
                  value={artist.street_address ?? ""}
                  name="street_address"
                  onChange={handleChange}
                  disabled={isFetching}
                />
              </div>
              <div className="my-5">
                <SideLabelInput
                  id="city"
                  type="city"
                  label="City"
                  value={artist.city ?? ""}
                  name="city"
                  onChange={handleChange}
                  disabled={isFetching}
                />
              </div>
              <div className="my-5">
                <SideLabelInput
                  id="state"
                  type="state"
                  label="State / Province"
                  value={artist.state ?? ""}
                  name="state"
                  onChange={handleChange}
                  disabled={isFetching}
                />
              </div>
              <div className="my-5">
                <SideLabelInput
                  id="zip_code"
                  type="zip_code"
                  label="Zip / Postal Code"
                  value={artist.zip_code ?? ""}
                  name="zip_code"
                  onChange={handleChange}
                  disabled={isFetching}
                />
              </div>
            </div>
            <fieldset className="flex text-xs">
              <div className="w-1/3">
                <legend>Active</legend>
              </div>
              <div>
                <input
                  checked={!artist.active ?? ""}
                  onChange={handleBooleanChange}
                  type="radio"
                  id="false"
                  name="active"
                  value=""
                />
                <label htmlFor="false" className="pl-2 pr-8">
                  No
                </label>
              </div>
              <div>
                <input
                  checked={artist.active}
                  onChange={handleBooleanChange}
                  type="radio"
                  id="true"
                  name="active"
                  value={true}
                />
                <label htmlFor="true" className="pl-2">
                  Yes
                </label>
              </div>
            </fieldset>
            <fieldset className="flex text-xs">
              <div className="w-1/3">
                <legend>TM Tax Responsible</legend>
              </div>
              <div>
                <input
                  checked={!artist.ctm_tax_responsible ?? ""}
                  onChange={handleBooleanChange}
                  type="radio"
                  id="false"
                  name="ctm_tax_responsible"
                  value=""
                />
                <label htmlFor="false" className="pl-2 pr-8">
                  No
                </label>
              </div>
              <div>
                <input
                  checked={artist.ctm_tax_responsible}
                  onChange={handleBooleanChange}
                  type="radio"
                  id="true"
                  name="ctm_tax_responsible"
                  value={true}
                />
                <label htmlFor="true" className="pl-2">
                  Yes
                </label>
              </div>
            </fieldset>
            <hr className="my-3"></hr>
            <div className="flex justify-center mt-8">
              {errorMessage?.error && (
                <div className="text-red-500 pb-2">{errorMessage.error}</div>
              )}
            </div>
            <div className="flex justify-end my-4">
              <div className="flex-col float-right">
                <Button
                  text={isFetching ? `Loading ${"\u00A0"}` : "Save"}
                  type="submit"
                  form="submit"
                  value="Submit"
                  disabled={isFetching}
                  inProgress={isFetching}
                  className="button"
                ></Button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditArtist;
